import { ApolloError, FetchResult, useQuery } from '@apollo/client'
import { client } from '../../..'
import {
  CancelMeetingMutation,
  CancelMeetingMutationVariables,
  Change,
  MeetingsByManagerQuery,
  MeetingsByManagerQueryVariables,
  ObjectivePayment,
  OneOffPayment,
} from '../../../types'
import { MEETINGS_BY_MANAGER } from '../meeting.graphql'
import { genericUpdateQueryCache } from '../../genericUpdateQueryCache'
import { SUBORDINATES_BY_EMPLOYEE } from '../../employee/Queries'
import { ObjectivePaymentByMeeting } from '../../objectivePayments/ObjectivePaymentQuery'
import { INVITE_OPTIONS } from '../../invite-options/inviteOptions'
import { CANCEL_MEETING } from '../MeetingMutations'
import { FIND_ONE_OFF_PAYMENTS_BY_MEETING_ID } from '../../one-off-payment/OneOffPaymentQueries'
import ContractQueries from '../../contract/ContractQueries'

export default (
  managerId: string,
  disputedMeetingsIncluded = false,
): {
  data: MeetingsByManagerQuery | undefined
  loading: boolean
  error: ApolloError | undefined
  cancelMeeting: (meetingId: number, employeeId: number, reasonForMeetingCancel: string) => Promise<FetchResult<CancelMeetingMutation>>
  startPolling: (pollInterval: number) => void
  stopPolling: () => void
} => {
  const { data, loading, error, startPolling, stopPolling } = useQuery<MeetingsByManagerQuery, MeetingsByManagerQueryVariables>(
    MEETINGS_BY_MANAGER,
    {
      variables: {
        managerId: managerId,
        disputedMeetingsIncluded,
      },
      fetchPolicy: 'network-only',
    },
  )

  const cancelMeeting = (meetingId: number, employeeId: number, reasonForMeetingCancel: string) => {
    return client.mutate<CancelMeetingMutation, CancelMeetingMutationVariables>({
      mutation: CANCEL_MEETING,
      variables: {
        meetingId: meetingId,
        employeeId: employeeId,
        reasonForMeetingCancel: reasonForMeetingCancel,
      },
      update: (store, _) => {
        genericUpdateQueryCache<MeetingsByManagerQuery, MeetingsByManagerQueryVariables>(
          store,
          { id: meetingId },
          MEETINGS_BY_MANAGER,
          { managerId: managerId },
          'meetingsByManager',
          'delete',
        )

        // Delete possible cached content from meeting which are deleted in the server
        store.writeQuery<{ contractChangesByEmployee: Change[] }>({
          query: ContractQueries.ContractChanges,
          variables: { employeeId: employeeId },
          data: { contractChangesByEmployee: [] },
        })
        store.writeQuery<{ objectivePaymentByMeeting: ObjectivePayment | undefined }>({
          query: ObjectivePaymentByMeeting,
          variables: { meetingId: meetingId },
          data: { objectivePaymentByMeeting: undefined },
        })
        store.writeQuery<{ oneOffPaymentByMeetingId: OneOffPayment | undefined }>({
          query: FIND_ONE_OFF_PAYMENTS_BY_MEETING_ID,
          variables: { meetingId: meetingId },
          data: { oneOffPaymentByMeetingId: undefined },
        })
      },
      refetchQueries: [
        {
          query: SUBORDINATES_BY_EMPLOYEE,
          variables: { employeeId: managerId, withMeetingInfo: true },
        },
        {
          query: INVITE_OPTIONS,
          variables: { employeeId: employeeId },
        },
      ],
    })
  }

  return { data, loading, error, cancelMeeting, startPolling, stopPolling }
}
